<template>
  <div class="landing-page-v2">
    <div class="hero">
      <div class="container">
        <div class="row">
          <div class="col col-7">
            <h1>
              <span class="highlight yellow">
                Learn
              </span>
              from Experts,<br>
              <span class="highlight blue">
                Become
              </span>
              an Expert
            </h1>
            <div class="subtitle">
              Become a certified
              <div class="animation" :class="{ animate: heroAnimation.animate }">
                {{ heroAnimation.strings[heroAnimation.index] }}
              </div>
              and double your salary within a year with our practice-based courses taught by instructors from top-tier companies.
            </div>
            <Companies
              v-if="companies && companies.length"
              :companies="companies" title="" no-fill
            />
          </div>
          <div class="col col-5">
            <div class="landing-card">
              <div class="card-title">
                Get a Premium Annual Plan
              </div>
              <PromoCard
                v-if="promoCardOptions" :options="promoCardOptions" v-on="$listeners"
                @subscribe="subscribe"
              >
                <div slot="label" class="label">
                  Save
                  <br>
                  <span class="bold">57%</span>
                </div>
                <div slot="regular-price" class="regular-price">
                  {{ prices.basePrice | formatCurrency(prices.currency, { minimumFractionDigits: 0 }) }}
                </div>
                <div slot="discounted-price" class="discounted-price">
                  {{ prices.price | formatCurrency(prices.currency, { minimumFractionDigits: 0 }) }}
                </div>
              </PromoCard>
              <Badges text-mode />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="section second">
      <div class="instructors-slider">
        <SliderInfinite :offset="0" heading="h2" :title="instructorsTitle">
          <div
            v-for="instructor in instructors"
            :key="instructor.id"
            slot="slide"
          >
            <nuxt-link :to="`/instructors/${instructor.slug}`">
              <img :src="instructorImage(instructor)" :alt="instructor.name">
            </nuxt-link>
          </div>
        </SliderInfinite>
      </div>
      <div class="container instructors-footer">
        <Companies
          v-if="companiesSmall && companiesSmall.length"
          :companies="companiesSmall" title="Trusted by employees from:" no-fill
        />
        <div class="trustpilot-review">
          <IconTrustpilot />
          <Rating :reviews="{ rating: 4.9 }" />
        </div>
        <div class="btn-holder">
          <button
            class="btn-primary btn-lg"
            :data-event="`${$route.path.replace(/\//g, '')}_v1_instructors.click`"
            @click="subscribe"
          >
            Learn at 57% OFF
          </button>
        </div>
      </div>
    </div>
    <div class="section stats">
      <StatsIcons
        class="container"
        :stats="page.stats" colored auto-width
        :order="['students', 'courses', 'projects']"
      />
    </div>
    <div class="section courses-extended">
      <div class="head container">
        <h2>Transform into a <span class="highlight yellow">Must-Hire</span> Professional</h2>
        <h3 class="muted">
          What’s your ideal job?
        </h3>
        <ul>
          <li
            v-for="(g, idx) in coursesGroups" :key="idx" :class="{ active: idx === courseGroupIdx }"
            @click="switchCourseGroup(idx)"
          >
            {{ g.name }}
          </li>
        </ul>
        <p class="description">
          Become a certified
          <span class="highlight blue">{{ courseGroup.description.title }}<span class="mobile-only">,</span></span><span class="desktop-only">,</span> earning
          <span class="static-salary-container">
            <span class="animation-salary" :class="{ animated: salaryAnimation.animatedOnScroll }">
              <span
                class="holder animation" :class="{ active: salaryAnimation.activate }"
                :style="`--width: ${salaryAnimation.width || 188}px`"
                v-html="salary"
              />
            </span>
          </span>
        </p>
      </div>
      <Courses :courses="courses" :offset="-2" heading="h3" />
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}_v1_courses.click`"
          @click="subscribe"
        >
          Start at 57% OFF
        </button>
      </div>
    </div>
    <div class="section master">
      <div class="container">
        <div class="row">
          <div class="col col-6">
            <h3>Master Essential Data and AI Skills</h3>
            <p class="muted">
              Become fluent in the leading tools employers look for, practice your skills with interactive exercises, and apply your knowledge to real-world projects.
            </p>
          </div>
          <div class="col col-6">
            <img :src="require(`../assets/images/landing/lp-sl-m-master${$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`)">
          </div>
        </div>
      </div>
    </div>
    <div class="section graduations">
      <Videos v-if="$config.platform === 'ds'" :videos="videos" :video-js-loaded="videoJsLoaded">
        <template slot="title">
          Join <LogoExtended />
          <p>Trusted by Over <span class="highlight yellow">3 Million</span> Learners Worldwide</p>
        </template>
      </Videos>
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          :data-event="`${$route.path.replace(/\//g, '')}v1_testimonials.click`"
          @click="subscribe"
        >
          Enroll Now
        </button>
      </div>
    </div>
    <div class="section dream">
      <div class="container">
        <h2>Your <span class="highlight yellow">Dream Career</span> Is Just a Click Away</h2>
        <p class="title-description">
          Unlock Your Earning Potential Today with<br>365 Data Science
        </p>
        <div class="row">
          <div class="col col-4">
            <IconDreamLearn />
            <p class="title">
              Learn from the Best
            </p>
            <p class="description">
              Gain skills and knowledge from industry leaders from Meta, Google, Netflix, Spotify, etc.
            </p>
          </div>
          <div class="col col-4">
            <IconDreamExperience />
            <p class="title">
              Gain Real-World Expertise
            </p>
            <p class="description">
              Acquire job-ready skills employers seek and build an outstanding data science portfolio.
            </p>
          </div>
          <div class="col col-4">
            <IconDreamJob />
            <p class="title">
              Land Your Dream Job
            </p>
            <p class="description">
              Leverage our extensive career resources to land a competitive salary role in data science.
            </p>
          </div>
        </div>
      </div>
      <div class="btn-holder">
        <button
          class="btn-primary btn-lg"
          data-direct-send
          :data-event="`${$route.path.replace(/\//g, '')}v1_dream.click`"
          @click="subscribe"
        >
          Start at 57% OFF
        </button>
      </div>
    </div>
    <section class="section landing-footer">
      <div class="container">
        <div class="row">
          <div class="col col-8 offset-2">
            <div class="head">
              <h3 class="h2">
                Invest in <span class="highlight yellow">Your Data Science Career</span> Now
              </h3>
              <p class="title-description">
                Get a 365 Data Science Annual Plan at a
              </p>
              <p class="title-description-2">
                <IconBadgePercent /> 57% Discount <IconBadgePercent />
              </p>
            </div>
            <div class="features-list">
              <ul>
                <li v-for="(feature, idx) in footerFeatures" :key="idx">
                  <IconCheckAuto />
                  {{ feature.name }}
                </li>
              </ul>
            </div>
            <div class="btn-holder">
              <button
                class="btn-primary btn-lg"
                :data-event="`${$route.path.replace(/\//g, '')}v1_footer.click`"
                @click="subscribe"
              >
                Save 57%
              </button>
            </div>
            <Badges class="badges pricing" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import { loadHotJar } from '@itbpbg/lms-components/src/utils/helper';
import Companies from '@itbpbg/lms-components/src/Campaigns/Companies';
import Badges from '@itbpbg/lms-components/src/Pricing/Badges';
import SliderInfinite from '@itbpbg/lms-components/src/components/SliderInfinite';
import Rating from '@itbpbg/lms-components/src/Courses/Rating';
import Videos from '@itbpbg/lms-components/src/Campaigns/Videos';
import PromoCard from './Landing/PromoCard';
import Courses from './Campaigns/Courses';
import StatsIcons from './Campaigns/StatsIcons';
import IconTrustpilot from '../assets/svg/icon-trustpilot.svg?vue-component';
import IconDreamLearn from '../assets/svg/icon-dream-learn.svg?vue-component';
import IconDreamExperience from '../assets/svg/icon-dream-experience.svg?vue-component';
import IconDreamJob from '../assets/svg/icon-dream-job.svg?vue-component';
import IconBadgePercent from '../assets/svg/icon-badge-percent.svg?vue-component';
import IconCheckAuto from '../assets/svg/icon-list-check-auto.svg?vue-component';
import LogoExtended from '../assets/svg/365-logo-extended.svg?vue-component';

export default {
  components: {
    Companies,
    Badges,
    SliderInfinite,
    PromoCard,
    Courses,
    StatsIcons,
    IconTrustpilot,
    Rating,
    Videos,
    IconDreamLearn,
    IconDreamExperience,
    IconDreamJob,
    IconBadgePercent,
    IconCheckAuto,
    LogoExtended,
  },
  mixins: [global],
  props: {
    url: { type: String, default: '', required: true },
    seo: { type: Object, required: true },
    userInfo: { type: Object, default: () => ({}), required: false },
    version: { type: String, default: '0', required: false },
  },
  data() {
    return {
      page: this.seo,
      instructorsTitle: 'Learn Data Science with <span class="highlight yellow">Instructors</span> from<br><span class="smaller">Meta, Netflix, Google, Spotify, IKEA, etc.</span>',
      videoJsLoaded: false,
      videos: [
        {
          name: 'Lee McFarling',
          image: 'lee',
          from: 'USA',
          id: 6315230915112,
          quote: 'After 6 months of learning with 365 Data Science, I was getting job interviews as a business analyst or a data analyst.',
          job: 'Data Analyst, Astreya',
        },
        {
          name: 'Meghan Verschoor',
          image: 'meghan',
          from: 'Canada',
          id: 6315228912112,
          quote: 'I find the platform really easy to follow through with and learn the skills that I needed to apply to my job. I highly recommend it.',
          job: 'Director, Data Science & Management, Turnstone Biologics',
        },
        {
          name: 'Nada Abdulwahab',
          image: 'nada',
          from: 'Egypt',
          id: 6315228795112,
          quote: '365 gave me the basics to start my career […] and still helps me enhance my skills and push my career to a higher level.',
          job: 'Senior Data Engineer, VOIS',
        },
        {
          name: 'Julián Elizondo',
          image: 'julián',
          from: 'Mexico',
          id: 6343656185112,
          quote: 'If it wasn’t for 365 Data Science and the confidence it gave me, I wouldn’t have landed my current job.',
          job: 'Tableau Developer, Schneider Electric',
        },
        {
          name: 'Mihaela Paparinova',
          image: 'mihaela',
          from: 'Bulgaria',
          id: 6341101204112,
          quote: 'The program helped me excel in my studies and deepen my understanding of niche data science concepts.',
          job: 'Data Science Student, Sofia University',
        },
        {
          name: 'Ernest Asena',
          image: 'ernest',
          from: 'Kenya',
          id: 6315231920112,
          quote: 'Eight months into the program, I started applying for entry-level positions, and I landed a job as a junior data scientist.”',
          job: 'Data Insights Analytics Cloud Engineer, AWS',
        },
        {
          name: 'Ahmed Eldessouky',
          image: 'ahmed',
          from: 'Egypt',
          id: 6327293863112,
          quote: 'I was impressed with the quality of the video lessons, and the topics and different tools the program covers.',
          job: 'Business Developer, Squadio',
        },
      ],
      heroAnimation: {
        strings: [
          'data scientist',
          'data analyst',
          'business analyst',
          'ML engineer',
        ],
        index: 0,
        animate: false,
      },
      salaryAnimation: {
        activate: false,
        animatedOnScroll: false,
      },
      companies: [
        { icon: 'meta-new' },
        { icon: 'g' },
        { icon: 'spotify' },
        { icon: 'netflix' },
        { icon: 'ikea' },
        { icon: 'coca-cola' },
        { icon: 'bbc' },
        { icon: 'pwc-new' },
        { icon: 'morgan-stanley' },
      ],
      companiesSmall: [
        { icon: 'oracle' },
        { icon: 'amazon' },
        { icon: 'walmart' },
        { icon: 'alphabet' },
        { icon: 'meta-new' },
      ],
      promoCardOptions: {
        labelText: 'Exclusive Offer',
        discountedPriceCharge: '/year',
        billedText: 'Billed annually',
        countdown: false,
        buttonText: 'Save 57% Now',
        subscribeClass: 'subscribe-annual',
        promoPlanType: 'annual',
        version: '_v1',
      },
      instructors: [
        {
          name: 'Davis Balaba',
          slug: 'davis-balaba',
        },
        {
          name: 'Danielle Thé',
          slug: 'danielle-the',
        },
        {
          name: 'Bernard Marr',
          slug: 'bernard-marr',
        },
        {
          name: 'Anastasia Kuznetsova',
          slug: 'anastasia-kuznetsova',
        },
        {
          name: 'Jeff Lee',
          slug: 'jeff-li-1',
        },
        {
          name: 'Shashank Kalanithi',
          slug: 'shashank-kalanithi-1',
        },
        {
          name: 'Lauren Newbould',
          slug: 'lauren-newbould-1',
        },
        {
          name: 'Olivier Maugain',
          slug: 'olivier-maugain',
        },
        {
          name: 'Randy Rosseel',
          slug: 'randy-rosseel',
        },
        {
          name: 'Tina Huang',
          slug: 'tina-huang',
        },
        {
          name: 'Ken Jee',
          slug: 'ken-jee',
        },
      ],
      courseGroupIdx: 0,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    // const brightcoveSrc = [
    //   { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
    //   { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
    // ];

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
        { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@700,600&display=swap' },
      ],
      meta: this.metaProps({ ...this.page }),

      script: [
        // ...brightcoveSrc,
        linkedData,
        {
          src:
            'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js',
          async: true,
        },
      ],
    };
  },
  computed: {
    coursesGroups() {
      return this.page.coursesGroups || [];
    },
    courseGroup() {
      return this.coursesGroups[this.courseGroupIdx] || {};
    },
    courses() {
      return (this.page.courses || []).filter((c) => this.courseGroup.courses.includes(c.id)) || [];
    },
    salary() {
      const [salary, year] = this.courseGroup.description.earn.split('/');
      const salaryBolded = `<span class="letter bold">${salary.split('').join('</span><span class="letter bold">')}</span>`;
      const yearNormal = `<span class="letter">${year.split('').join('</span><span class="letter">')}</span>`;

      return `${salaryBolded}<span class="letter">/</span>${yearNormal}`;
    },
    limitedOfferOptions() {
      const price = this.page && this.page.prices.price ? this.page.prices.price : 119;
      const baseAnnualPrice = this.page && this.page.prices.basePriceComparison ? this.page.prices.basePriceComparison : 432;

      return {
        preTitleSlot: true,
        htmltitle: 'asd',
        promoPlanType: 'annual',
        comparisonPlanType: 'monthly',
        comparisonPlanBilledText: 'Billed Monthly',
        promoPlanBilledText: 'Billed Annually',
        regularPrice: `$${this.page.prices.basePrice}`,
        discountedPrice: `$${price}`,
        billedComparisonPrice: `$${baseAnnualPrice}`,
        discountedPriceCharge: '/year',
        billedComparisonCharge: '/month',
        promoPlanSubscribeClass: 'subscribe-annual',
        comparisonSubscribeClass: 'subscribe-monthly',
        promoPlanButtonText: 'Save 57%',
        buttonClass: 'submit',
        comparisonPlanButtonText: 'Sign up Now',
        vat: 'VAT may be applied',
        badges: this.$config.platform === 'ds',
      };
    },
    footerFeatures() {
      const data = [
        { name: `${this.page.stats.rounded.courses} data and AI courses` },
        { name: 'Employer-recognized certificates' },
        { name: 'World-class instructors' },
        { name: 'Resume builder' },
        { name: 'Engaging bite-sized lessons' },
        { name: 'Resume feedback' },
        { name: `${this.page.stats.rounded.exercises}+ practical exercises` },
        { name: 'Portfolio advice' },
        { name: 'Structured career tracks' },
        { name: 'Social channels for peer discussions' },
        { name: 'Custom learning path' },
        { name: 'A gamified learning platform' },
        { name: 'Real-world data projects' },
        { name: '30-day refund' },
        { name: 'Hands-on exams' },
        { name: 'Price-lock guarantee' },
      ];

      if (this.$store.state.layout === 'mobile') {
        return [
          ...data.filter((v, i) => i % 2 === 0),
          ...data.filter((v, i) => i % 2 === 1),
        ];
      }

      return data;
    },
    prices() {
      return this.seo.prices;
    },
  },
  async mounted() {
    if (this.$config.platform === 'ds') {
      loadHotJar();
    }

    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJsLoaded = true;
    // Hero animation
    setInterval(() => {
      this.heroAnimation.animate = true;
      setTimeout(() => {
        this.heroAnimation.index = (this.heroAnimation.index + 1) % this.heroAnimation.strings.length;
      }, 300);

      setTimeout(() => {
        this.heroAnimation.animate = false;
      }, 600);
    }, 3000);

    window.addEventListener('scroll', this.handleScroll);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.handleScroll));
    this.handleScroll();
  },
  methods: {
    handleScroll() {
      if (process.client) {
        const salary = this.$el.querySelector('.animation-salary');
        if (salary && !this.salaryAnimation.animatedOnScroll) {
          const salaryBounds = salary.getBoundingClientRect();
          if (salaryBounds.top < window.innerHeight * 0.7) {
            this.switchCourseGroup(0, true);
            this.salaryAnimation.animatedOnScroll = true;
          }
        }
      }
    },
    instructorImage(instructor) {
      return require(`../assets/images/landing/lp-sl-m-${instructor.slug}${this.$store.state.layout === 'mobile' ? '-mobile' : ''}.webp`);
    },
    switchCourseGroup(idx, force = false) {
      if (idx === this.courseGroupIdx && !force) {
        return;
      }

      if (this.salaryAnimation.activate) {
        this.salaryAnimation.activate = false;
      }

      let startAnimationAfter = 10;

      if (!force && this.$store.state.layout === 'mobile') {
        const coursesSlider = this.$el.querySelector('.courses-extended .infinite-carousel');
        if (coursesSlider) {
          startAnimationAfter = 500;
          coursesSlider.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }

      this.salaryAnimation.animatedOnScroll = true;

      setTimeout(() => {
        this.courseGroupIdx = idx;
        this.$nextTick(() => {
          let width = 0;
          const holder = this.$el.querySelector('.animation-salary .holder');
          const letters = holder.querySelectorAll('.letter');
          for (let idx2 = 0; idx2 < letters.length; idx2 += 1) {
            const letter = letters[idx2].getBoundingClientRect();
            width += letter.width;
          }
          this.salaryAnimation.width = width + 2; // add border to calculations
          this.salaryAnimation.activate = true;
          setTimeout(() => {
            for (let idx3 = 0; idx3 < letters.length; idx3 += 1) {
              const letter = letters[idx3];
              setTimeout(() => {
                letter.classList.add('show');
              }, idx3 * 100);
            }
          }, 200);
        });
      }, startAnimationAfter);
    },
    subscribe() {
      const plan = this.$store.getters['subscriptions/annual'];
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: 'annual', coupon: this.page.coupon },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes heroAnimation {
  0% {
    transform: rotateX(0deg);
  }

  50% {
    transform: rotateX(90deg);
  }

  100% {
    transform: rotateX(0deg);
  }
}

@include bp($bp-desktop) {
  .mobile-only,
  .laptop-only {
    display: none !important;
  }
}

.landing-page-v2 {
  background-color: var(--main-dark);
  color: var(--main-light);
  min-height: 100vh;

  :not(.hero) .btn-primary {
    width: 350px;
  }

  h1 {
    font-size: 48px;
    font-weight: 500;
    line-height: 1.1;
    margin-bottom: 40px;
  }

  .muted {
    color: var(--main-light-shade-4);
  }

  .highlight,
  ::v-deep .highlight {
    &.blue {
      color: var(--accent);
    }

    &.yellow {
      color: var(--main-yellow);
    }
  }

  .subtitle {
    font-size: 24px;
    font-weight: 500;
  }

  .animation {
    font-family: "Source Code Pro", monospace;
    font-optical-sizing: auto;
    font-weight: 600;
    font-style: normal;
    display: inline-block;
    font-size: 24px;
    color: var(--main-dark);
    background-color: #F0F0F2;
    border: 1px solid #ABAFB5;
    border-radius: 4px;
    width: 248px;
    height: 32px;
    text-align: center;
    transform-style: preserve-3d;

    &.animate {
      animation: heroAnimation 0.6s ease-in-out;
    }
  }

  .static-salary-container {
    width: 188px;
    display: inline-block;
  }

  .animation-salary {
    vertical-align: middle;
    display: inline-block;
    width: 188px;

    .holder {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      width: 0;

      &.animation {
        text-align: left;
        line-height: 30px;
      }

      &.active {
        transition: width 0.5s;
        width: var(--width, 188px);
      }
    }

    ::v-deep {
      .letter {
        font-weight: normal;
        opacity: 0;
        transition: all 0.1s linear;

        &.show {
          opacity: 1;
        }

        &:first-child {
          padding-left: 4px;
        }

        &:last-child {
          padding-right: 4px;
        }

        &.bold {
          font-weight: 700;
        }
      }
    }
  }

  .section {
    background-size: cover;
    background-position: bottom;
    padding-top: 100px;
    padding-bottom: 100px;

    &.second {
      background-image: url('../assets/images/landing/lp-sl-m-bg-slider.webp');

      ::v-deep .infinite-head .h4 {
        text-align: center;
        font-size: 40px;

        .smaller {
          font-size: 32px;
        }
      }
    }
  }

  .hero {
    min-height: 740px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url('../assets/images/landing/lp-sl-m-bg-hero.webp');
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;

    ::v-deep .trusted-companies {
      margin-top: var(--spacing-36);
      padding-top: var(--spacing-36);
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        flex-wrap: wrap;
        justify-content: center;
        row-gap: 32px;
        column-gap: 66px;

        .logo {
          svg {
            height: 24px;
            color: var(--main-light-shade-3);

            g[fill] {
              fill: var(--main-light-shade-3);
            }

            &.pwc {
              height: 28px;
            }
          }
        }
      }
    }

    .landing-card {
      padding-left: 50px;

      .card-title {
        font-size: 24px;
        font-weight: 500;
        margin-bottom: 16px;
        text-align: center;
        text-shadow: 0 1px 16.4px rgba(0, 0, 0, 0.8);
      }

      ::v-deep {
        .card {
          background-color: rgba(0, 12, 31, 0.80);
          border-radius: 10px;
          box-shadow: 0 0 15.8px 0 rgba(0, 255, 255, 0.8);
          position: relative;

          .discounted-price,
          .subtitle {
            color: var(--main-light);
          }

          .discounted-price {
            font-weight: 400;
          }

          .label {
            color: var(--main-dark);
            font-size: 24px;
            font-weight: 500;
            line-height: 1.1;
            text-align: center;
            background-image: url('../assets/images/landing/lp-sl-m-bg-ribbon.webp');
            background-size: contain;
            background-repeat: no-repeat;
            position: absolute;
            top: -5px;
            right: 36px;
            padding: 16px 10px 34px;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;

            .bold {
              font-size: 32px;
              font-weight: 600;
            }
          }
        }

        .badge-wrap {
          display: block;
          text-align: center;
          line-height: 1.5;
          font-weight: 400;
          color: var(--main-light-shade-3);

          .price-lock {
            display: inline-block;
            padding-right: 20px;

            .tooltip-holder {
              top: 0;
            }
          }
        }
      }
    }
  }

  .instructors-slider {
    ::v-deep {
      .infinite-carousel .carousel .inner > * {
        width: 210px;
        margin-right: 30px;

        img {
          display: block;
          border-radius: 20px;
        }
      }
    }
  }

  .instructors-footer {
    ::v-deep .trusted-companies {
      margin-top: 30px;
      padding-top: 32px;
      border-color: rgba(255, 255, 255, 0.24);

      .logos {
        .logo {
          svg {
            height: 30px;

            &,
            g[fill] {
              color: var(--main-light-shade-3);
            }

            path[fill],
            g[fill] {
              fill: var(--main-light-shade-3);
            }
          }
        }
      }
    }

    .trustpilot-review {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 40px;
      margin-bottom: 30px;

      svg {
        height: 40px;
        margin-right: 26px;
      }

      ::v-deep .course-rating {
        font-size: 28px;
        font-weight: 500;
        color: var(--main-light-shade-4);

        .rating {
          margin-right: 20px;
        }

        .rating-stars {
          height: auto;

          svg {
            width: 38px;
            height: 38px;
          }
        }
      }
    }
  }

  .btn-holder {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }

  .stats {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.20) 0.94%, rgba(86, 191, 199, 0.00) 100%), #000C1F;

    ::v-deep .stats-icons {
      margin-top: 0;

      > div {
        align-items: center;
      }

      svg {
        width: 90px;
        height: 90px;
      }
    }
  }

  .courses-extended {
    .head {
      text-align: center;

      h2 {
        font-size: 40px;
      }

      h3 {
        margin: 40px 0;
      }

      ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 16px;
        padding-left: 0;

        li {
          padding: 20px;
          font-size: 18px;
          line-height: 1.1;
          color: var(--main-light);
          background-color: var(--main-dark-light-1);
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover,
          &.active {
            border-color: var(--accent);
            background-color: var(--main-dark-light-2);
          }
        }
      }

      .description {
        font-size: 24px;
        font-weight: 500;
        margin-top: 60px;
        margin-bottom: 0;
      }
    }
  }

  ::v-deep .infinite-carousel {
    .controls {
      margin-top: 0;
    }

    .carousel {
      padding-top: 10px;
      margin-top: -10px;
      padding-bottom: 10px;
      margin-bottom: -10px;

      .card {
        margin: 0 var(--spacing-30) 0 0;
        width: 370px;
        vertical-align: top;

        .course-card-container {
          min-height: 422px;
        }

        &:not(.course-card) {
          flex-direction: column;
          align-items: flex-start;
          height: auto;
          border-radius: 0;

          .image-wrapper img {
            display: block;
            max-width: 100%;
            max-height: 100%;
            border-radius: 7px;
          }

          .card-title {
            margin-top: var(--spacing-20);
            font-size: 32px;
            color: var(--main-light);
          }

          .card-desc {
            color: var(--main-dark-shade-4);
            font-size: 21px;
            line-height: 140%;
          }
        }

        &.course-card {
          .course-card-image {
            height: 220px;
          }

          .course-card-body {
            padding: var(--spacing-20) var(--spacing-24) 20px;

            .course-name {
              margin-bottom: var(--spacing-8);
            }

            .course-rating {
              color: var(--main-light);
            }

            .info {
              color: var(--main-light);
              margin: 0;

              .dot {
                background: var(--main-light);
              }
            }
          }
        }
      }
    }
  }

  .master {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.20) 0.94%, rgba(86, 191, 199, 0.00) 100%), #000C1F;

    .muted {
      font-size: 21px;
    }

    img {
      display: block;
      max-width: 100%;
    }
  }

  .graduations {
    background-image: url('../assets/images/landing/lp-sl-m-bg-graduations.webp');
    background-size: cover;
    background-position: bottom;

    ::v-deep {
      .title-highlight {
        font-size: 32px;

        p {
          font-size: 28px;
        }

        svg {
          height: 38px;
        }
      }
    }
  }

  .dream {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.20) 0.94%, rgba(86, 191, 199, 0.00) 100%), #000C1F;
    text-align: center;

    .title-description {
      font-size: 32px;
      font-weight: 500;
      margin: 0;
    }

    .row {
      margin-top: 60px;
      margin-bottom: 18px;
    }

    .col {
      svg {
        color: var(--accent);
      }

      .title {
        font-size: 28px;
        font-weight: 500;
        color: var(--accent);
      }

      .description {
        font-size: 18px;
        font-weight: 400;
        line-height: 1.5;
      }
    }
  }

  .landing-footer {
    background-image: url('../assets/images/landing/lp-sl-m-bg-footer.webp');
    background-size: cover;
    background-position: center;
    text-align: center;

    .h2 {
      margin-bottom: 32px;
    }

    p {
      margin: 0;
    }

    .title-description {
      font-size: 32px;
      font-weight: 500;
    }

    .title-description-2 {
      font-size: 40px;
      font-weight: 600;
      color: var(--accent);
      margin-top: 12px;
    }

    .head {
      padding-bottom: 32px;
      margin-bottom: 60px;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 370px;
        height: 1px;
        background-color: #6F7379;
      }
    }

    .features-list {
      margin-bottom: 40px;

      ul {
        list-style: none;
        padding: 0;
        display: flex;
        flex-wrap: wrap;

        li {
          flex: 0 0 50%;
          display: flex;
          align-items: center;
          font-size: 18px;
          font-weight: 400;
          color: var(--main-light);
          margin-bottom: 16px;

          svg {
            margin-right: 10px;
            color: var(--accent);
          }
        }
      }
    }

    .btn-holder {
      margin-bottom: 60px;
    }
  }

  @include bp($bp-laptop) {
    .section.second {
      ::v-deep .infinite-head .h4 {
        font-size: 32px;

        .smaller {
          font-size: 28px;
        }
      }
    }

    .courses-extended .head {
      ul li {
        font-size: 14px;
      }

      .description {
        font-size: 21px;
      }
    }

    .dream {
      .title-description {
        font-size: 28px;
      }

      .col {
        .title {
          font-size: 24px;
        }
      }
    }

    .landing-footer {
      .title-description {
        font-size: 28px;
      }

      .title-description-2 {
        font-size: 32px;
      }
    }
  }

  @include bp($bp-mobile) {
    .desktop-only {
      display: none !important;
    }

    .section {
      padding-top: 60px;
      padding-bottom: 60px;
      background-size: contain;
      background-position: bottom;
      background-repeat: no-repeat;
    }

    :not(.hero) .btn-primary {
      width: 100%;
      max-width: 327px;
    }

    h1 {
      font-size: 44px;
    }

    .subtitle {
      font-size: 20px;
    }

    .hero {
      background-image: url('../assets/images/landing/lp-sl-m-bg-hero-mobile.webp');
      padding-top: 108px;

      ::v-deep .trusted-companies {
        margin-top: 20px;
        padding-top: 20px;

        .logos {
          column-gap: 16px;

          .logo svg {
            height: 14px;
          }
        }
      }

      .landing-card {
        padding-left: 0;

        .card-title {
          font-size: 20px;
        }

        .badge-wrap {
          margin-top: 16px;
        }
      }
    }

    .section.second {
      background-image: url('../assets/images/landing/lp-sl-m-bg-slider-mobile.webp');
      margin-top: 60px;

      ::v-deep {
        .infinite-head .h4 {
          font-size: 32px;

          .smaller {
            font-size: 28px;
          }
        }

        .infinite-carousel .carousel .inner > * {
          width: 327px;
        }
      }
    }

    .instructors-footer {
      ::v-deep {
        .trusted-companies {
          margin-bottom: 30px;

          .title {
            margin-top: 0;
            margin-bottom: 30px;
          }
        }

        .trustpilot-review {
          margin-top: 0;
          align-items: baseline;

          > svg {
            height: 26px;
            flex: 0 0 auto;
          }

          .course-rating {
            font-size: 20px;

            .rating-stars svg {
              width: 25px;
              height: 25px;
            }
          }
        }
      }
    }

    .courses-extended {
      .head {
        ul {
          gap: 12px;
          justify-content: flex-start;
        }

        .description {
          margin-top: 32px;

          .highlight.blue {
            display: block;
          }
        }
      }

      .courses {
        padding-top: 32px;
      }

      ::v-deep .infinite-carousel .carousel .card {
        width: 327px;
      }
    }

    .master {
      text-align: center;
    }

    .graduations {
      background-image: url('../assets/images/landing/lp-sl-m-bg-graduations-mobile.webp');

      ::v-deep {
        .col {
          padding-left: 26px;
          padding-right: 26px;
        }
      }
    }

    .dream {
      h2 {
        font-size: 32px;
      }

      .title-description {
        font-size: 28px;
      }
    }

    .landing-footer {
      background-image: url('../assets/images/landing/lp-sl-m-bg-footer-mobile.webp');

      .h2 {
        font-size: 32px;
      }

      .title-description {
        font-size: 28px;
      }

      .title-description-2 {
        font-size: 32px;
      }

      .features-list ul {
        display: block;
        text-align: left;

        li {
          justify-content: flex-start;
        }
      }
    }
  }
}
</style>
